/**
 * ModelSelector component
 * Overlay for selecting LLM model
 */
import React, { useState } from 'react';
import { Box, Text, useInput } from 'ink';
import type { ModelWithUsage } from '../../../types/llm.types.js';

interface ModelSelectorProps {
    models: ModelWithUsage[];
    currentModel: string;
    onSelect: (modelId: string) => void;
    onCancel: () => void;
}

export const ModelSelector: React.FC<ModelSelectorProps> = ({
    models,
    currentModel,
    onSelect,
    onCancel,
}) => {
    const allowedModels = models.filter((m) => m.allowed);
    const currentIndex = allowedModels.findIndex((m) => m.id === currentModel);
    const [selectedIndex, setSelectedIndex] = useState(Math.max(0, currentIndex));

    useInput((input, key) => {
        if (key.escape) {
            onCancel();
            return;
        }

        if (key.upArrow) {
            setSelectedIndex((prev) => Math.max(0, prev - 1));
        } else if (key.downArrow) {
            setSelectedIndex((prev) => Math.min(allowedModels.length - 1, prev + 1));
        } else if (key.return) {
            const selected = allowedModels[selectedIndex];
            if (selected) {
                onSelect(selected.id);
            }
        }
    });

    if (allowedModels.length === 0) {
        return (
            <Box
                flexDirection="column"
                borderStyle="round"
                borderColor="red"
                padding={1}
                width={60}
            >
                <Text color="red" bold>
                    No models available
                </Text>
                <Text dimColor>Press Esc to close</Text>
            </Box>
        );
    }

    return (
        <Box
            flexDirection="column"
            borderStyle="round"
            borderColor="cyan"
            padding={1}
            width={70}
        >
            <Box marginBottom={1}>
                <Text bold color="cyan">
                    ⚙️  Select Model
                </Text>
            </Box>

            <Box flexDirection="column" marginBottom={1}>
                {allowedModels.map((model, i) => {
                    const isSelected = i === selectedIndex;
                    const isCurrent = model.id === currentModel;
                    const icon = isSelected ? '●' : '○';

                    let usageText = '';
                    if (model.dailyLimit !== undefined && model.usedToday !== undefined) {
                        usageText = ` (${model.usedToday}/${model.dailyLimit})`;
                    }

                    return (
                        <Box key={model.id} marginY={0}>
                            <Text
                                backgroundColor={isSelected ? 'cyan' : undefined}
                                color={isSelected ? 'black' : 'white'}
                            >
                                {isSelected ? '❯ ' : '  '}
                                {icon} {model.id}
                                {isCurrent ? ' ✓' : ''}
                                {usageText}
                            </Text>
                        </Box>
                    );
                })}
            </Box>

            <Box borderStyle="single" borderColor="gray" paddingX={1}>
                <Text dimColor>[↑↓] Navigate  [Enter] Select  [Esc] Cancel</Text>
            </Box>
        </Box>
    );
};
